/**
 * Лексика экрана прав — Блок 2 · Ф8 «Права доступа» · В-Э.
 *
 * Два места: блок «Права» в карточке человека и сводный список у супервайзера
 * (Р-200, вариант Б). Оба видит только супервайзер, поэтому здесь можно писать
 * «отнято» и «закрыто из-за ГИПа» прямым текстом: правило «на месте закрытого —
 * ничего» (Р-200) — про того, кому закрыто, а не про того, кто выдаёт.
 *
 * ⚠️ ЗАПРЕТОВ НЕТ (решение владельца 11.10.2026, Ф8 · В-У): положенное по
 * должности не отнимается ни у кого; руководителю отдела право добавляют и
 * убирают, администратору не настраивают ничего. Слов «Отнять» и «Вернуть» как
 * ДЕЙСТВИЙ в словаре больше нет, как нет и текстов о последствиях запрета и
 * подсказки «деньги отняты, а журнал оставлен». «Отнято» и «Закрыто из-за
 * ГИПа» оставлены как ПОДПИСИ состояния: так показывается строка запрета,
 * лежащая в базе с прежних времён, — её нельзя поставить, но можно сбросить.
 *
 * ⚠️ НАЗВАНИЯ ВОСЬМИ ПРАВ — из решений, а не придуманы здесь: таблица Р-202
 * («Итог круга: изменяемых прав — восемь…»), состав права № 7 — Р-185. Владелец
 * принял список буквой и названия поимённо не правил (Р-188, граница 5) —
 * менять их можно, но одним местом: этим словарём.
 *
 * `Record<UserRight, …>`, а не объект с произвольными ключами: девятое право
 * без строки здесь — ошибка сборки, а не пустая подпись на экране.
 */
import type { RightInertReason, UserRight } from '@aris/shared';

/** Название права — одинаковое в карточке, в списке и в диалоге. */
export const rightNameRu: Readonly<Record<UserRight, string>> = {
  PROJECT_ARCHIVE: 'Архивировать объекты и возвращать из архива',
  CONTRACT_SUM_READ: 'Видеть сумму договора',
  CONTRACT_WRITE: 'Вести договоры',
  EXPENSE_READ: 'Видеть расходы',
  EXPENSE_WRITE: 'Вести расходы',
  WORKLOAD_ALL_READ: 'Вся загруженность бюро',
  DASHBOARD_ORG_READ: 'Показатели бюро на дашборде',
  AUDIT_READ: 'Видеть общий журнал действий',
};

/**
 * Что видно в общем журнале — ОДИН перечень на оба места: подсказку права № 9
 * и окно подтверждения его выдачи (Р-251, находка 5; `SECURITY-REVIEW.md`, В-3).
 *
 * ⚠️ ПЕРЕЧЕНЬ — «В ТОМ ЧИСЛЕ», А НЕ ПОЛНЫЙ. Журнал хранит снимки строк целиком
 * (`apps/api/src/common/audit/redact.ts`: «PII such as email / phone /
 * fullName is DELIBERATELY KEPT»), и выписать каждое поле каждой модели здесь
 * значило бы завести вторую схему. Названо то, что выдающему надо знать.
 *
 * Источники пунктов:
 *   - суммы, расходы, фактические часы — Р-190; «оценка трудоёмкости скрыта» —
 *     `redact.ts`, `work: ['hoursEstimate']` (в снимке значение затёрто);
 *   - ФИО, должности, даты приёма, почты и телефоны — модель `User` в
 *     `AUDITED_MODELS` (поля `fullName`, `roleId`, `hireDate`, `email`,
 *     `phone` не маскируются); приглашения — модель `Invite` там же (затёрт
 *     только `tokenHash`);
 *   - кому какие права выданы и отняты — умолчание 1 Р-211, Р-224;
 *   - больничные и отпуска с заметками, тексты отчётов о часах, контакты и
 *     ИНН заказчиков, IP-адреса, события сброса паролей — перечень Р-224
 *     («Общий журнал руководителю отдела выдавать можно — как сделано…»);
 *   - «пароли и токены скрыты» — Р-224 и `SENSITIVE_FIELDS` в `redact.ts`.
 *
 * ⚠️ ПРЕЖДЕ ОБА ТЕКСТА НАЗЫВАЛИ ТОЛЬКО ДЕНЬГИ И ЧАСЫ — то есть ровно тот у́же
 * факт, на котором ошибся вопрос Р-190: выдающий не узнавал, что открывает
 * чужие больничные и телефоны.
 */
export const JOURNAL_CONTENTS_RU: readonly string[] = [
  'суммы и расходы',
  'фактические часы (оценка трудоёмкости работ скрыта)',
  'ФИО, должности и даты приёма сотрудников',
  'почты и телефоны сотрудников',
  'приглашения',
  'кому какие права выданы и отняты',
  'больничные и отпуска с заметками',
  'тексты отчётов о часах',
  'контакты и ИНН заказчиков',
  'IP-адреса',
  'события сброса паролей',
];
const JOURNAL_HIDDEN_RU = 'Пароли и токены в журнале скрыты.';

/**
 * Что право значит — простыми словами, обычно одной строкой.
 *
 * ⚠️ ИСКЛЮЧЕНИЕ — `AUDIT_READ`: его подсказка длинная намеренно. Право
 * открывает личные данные всего бюро, и перечень того, что именно, — часть
 * смысла права, а не украшение (Р-251, находка 5).
 */
export const rightHintRu: Readonly<Record<UserRight, string>> = {
  PROJECT_ARCHIVE: 'Отправлять объект в архив и возвращать его обратно.',
  CONTRACT_SUM_READ: 'Сумма договора в справочнике, в карточке объекта и в выгрузке.',
  CONTRACT_WRITE: 'Заводить и менять договоры. Работает только вместе с «Видеть сумму договора».',
  EXPENSE_READ: 'Расходы объекта: вкладка, итоги и выгрузка.',
  EXPENSE_WRITE: 'Вносить и менять расходы. Работает только вместе с «Видеть расходы».',
  WORKLOAD_ALL_READ: 'Загрузка и часы всех сотрудников, их отсутствия, поимённые часы по объектам.',
  DASHBOARD_ORG_READ:
    'Плитки и графики по всему бюро. Сводка по бюро и лента «Сроки» от этого права не зависят.',
  AUDIT_READ: `Журнал всех действий в системе — целиком, без фильтра. В нём видны в том числе: ${JOURNAL_CONTENTS_RU.join('; ')}. ${JOURNAL_HIDDEN_RU}`,
};

/** Почему строка с поправкой не действует. */
export const rightInertReasonRu: Readonly<Record<RightInertReason, string>> = {
  ROLE_CHANGED: 'Должность сменилась — поправка осталась от прежней.',
  LEVEL_MISMATCH: 'К этой должности такая поправка не применяется.',
  NOT_ACTIVE: 'Человек не действующий — приглашён или в архиве.',
};

/**
 * Где настраиваются права — одной строкой на все экраны (Ф8 · В-Т). Подписи —
 * как на экране: пункт меню «Сотрудники» (`root-layout.tsx`), щелчок по строке
 * открывает карточку (`onRowClick` в `employees-list.page.tsx`; подтверждено
 * на стенде), блок «Права» в карточке.
 *
 * Перед каждой «→» — неразрывный пробел (U+00A0): стрелка не начинает строку
 * при переносе (фикс-пак 1).
 */
const ARROW = '\u00a0→ ';
const PATH_TO_RIGHTS = [
  '«Сотрудники»',
  'нажмите на строку руководителя отдела',
  'блок «Права»',
].join(ARROW);

/** Первая строка блока у того, кому настраивать нечего (Ф8 · В-Т). */
const NOT_CONFIGURED = 'Этому человеку права не настраиваются.';

/** Куда идти вместо этого — администратору, сотруднику, супервайзеру. */
const WHERE_TO_ADD =
  'Добавлять права можно только руководителям отделов — откройте карточку руководителя отдела.';

export const rightsRu = {
  // ─── Блок в карточке человека ─────────────────────────────────────────────
  block: {
    title: 'Права',
    /**
     * Вводная — только у руководителя отдела: ему одному здесь есть что
     * сделать (Ф8 · В-Т). Заменила и прежнюю «что у человека добавлено сверх
     * должности», и «по должности этих прав нет»: две вводных подряд.
     * Число «шесть» сверяется тестом с `RIGHT_GRANTABLE_TO_MANAGER`; «до» —
     * потому что «вести» не добавляется без «видеть» (Р-195).
     */
    managerIntro:
      'Руководителю отдела можно добавить до шести прав сверх должности. «Добавить» — дать право, «Убрать» — забрать добавленное.',
    /**
     * Дописывается к вводной, только если в карточке есть строка с кнопкой
     * «Сбросить» (недействующая поправка, `actionOf` в модели) — иначе
     * говорить о старых настройках незачем.
     */
    managerIntroReset: 'Старые настройки снимаются кнопкой «Сбросить».',
    loading: 'Права загружаются',
    loadError: 'Не удалось загрузить права',
    loadErrorHint: 'Попробуйте ещё раз.',
    /** Список строк для экранного диктора; `{name}` — ФИО. */
    listAriaLabel: 'Права: {name}',

    /**
     * Администратору настраивать нечего (решение владельца 11.10.2026) —
     * сказано один раз, вместо восьми строк «По должности». Строй — как у
     * не-получателя (Ф8 · В-Т): что настраивать нечего, что положено, куда идти.
     */
    fixedByRoleLead: NOT_CONFIGURED,
    /**
     * ЧТО ИМЕННО положено по должности — одним абзацем с названием должности
     * (фикс-пак 1 В-Т склеил прежние «почему» и перечень). `{role}` — как в
     * сводном списке: `ROLE_NAMES[roleSlug] ?? roleName`.
     *
     * ⚠️ Текст ОДИН на четыре должности, и это проверено по правилам должности
     * на сервере (Ф8 · В-У, фикс-пак 1): шесть прав из восьми администратор
     * получает по УРОВНЮ, без оглядки на должность; два — «архивировать» и
     * «вести расходы» — техдиректор и BIM-менеджер на любом объекте, ГИП и его
     * помощник только на объекте, который ведёт ГИП (`canArchiveOrRestore`,
     * `canAddExpense`). Вторая фраза — про них.
     *
     * Ф8 · В-С: оговорка «настройка сервера `WORKLOAD_VIEW_LEVEL=employee`
     * закрывает загруженность всем администраторам» снята — значения больше
     * нет, и текст верен при любом значении настройки.
     */
    fixedByRole:
      'Должности «{role}» положено: архивировать объекты, видеть сумму договора, вести договоры, видеть и вести расходы, видеть всю загруженность бюро и показатели бюро на дашборде, читать общий журнал. ГИП и его помощник архивируют объекты и ведут расходы только на своих объектах.',
    fixedByRoleWhere: WHERE_TO_ADD,
    /**
     * У администратора всё же что-то лежит: запрет прежних времён (свой либо
     * ГИПа) либо недействующая поправка. Строки ниже — только они.
     *
     * ⚠️ Сверено по трём состояниям строки (`statusOf`, `actionOf` в модели,
     * фикс-пак 1 В-Т): «Отнято» — своя строка, закрывает право, «Сбросить»
     * возвращает его по должности; «Закрыто из-за ГИПа» — закрывает, но здесь
     * не снимается (Р-197), только в карточке ГИПа; «Не действует» — сама
     * ничего не закрывает (либо поправка мёртвая, либо «вести» без «видеть»),
     * причина написана в строке. Обещать «сбросьте — и будет по должности»
     * для всех трёх было бы неправдой.
     */
    legacyRows:
      'Ниже — старые настройки. «Отнято» и «Закрыто из-за ГИПа» закрывают право: «Отнято» снимается кнопкой «Сбросить», «Закрыто из-за ГИПа» — только в карточке ГИПа. У «Не действует» причина написана в строке.',
    /** Запасной построчный вид — если сервер пришлёт разное по строкам. */
    byRoleYes: 'По должности: есть',
    byRoleNo: 'По должности: нет',
    /**
     * Оговорка к «по должности есть»: ГИП и его помощник пишут расходы только
     * в объекты, которые ведёт ГИП (Р-84) — право по должности у них не «везде».
     */
    byRoleOwnObjects: 'По должности — только на своих объектах.',

    /** Кто и когда менял строку; `{who}` — ФИО, `{when}` — дата и время. */
    changedBy: 'Изменил: {who}, {when}',
    changedAt: 'Изменено: {when}',
    /**
     * То же у СБРОШЕННОЙ строки (эффекта нет, право снова «По должности»):
     * «Изменил» рядом с «По должности» читался как «поправка действует».
     * Подпись остаётся — по ней видно, кто стёр галочку.
     */
    resetBy: 'Сброшено: {who}, {when}',
    resetAt: 'Сброшено: {when}',

    /** Действует с перехода, а не сразу (Р-198); что сбрасывает (Р-198, П3). */
    footnote:
      'Изменения человек увидит при следующем переходе между страницами. Смена должности, возврат в приглашённые и повторное приглашение сбрасывают всё добавленное.',
  },

  /**
   * Текущее состояние строки — подпись значка.
   *
   * ⚠️ `ROLE` («По должности») у РУКОВОДИТЕЛЯ ОТДЕЛА не печатается: по
   * должности у него этих прав нет, и значок читался как «право есть». Ему —
   * `roleManager` ниже. `ROLE` остаётся запасным для строки, у которой право
   * по должности действительно есть.
   */
  status: {
    ROLE: 'По должности',
    GRANTED: 'Добавлено',
    DENIED: 'Отнято',
    INHERITED_DENY: 'Закрыто из-за ГИПа',
    INERT: 'Не действует',
  },

  /**
   * Значок строки руководителя отдела, пока ничего не добавлено (состояние
   * `ROLE`): добавляемому праву — «Не добавлено», двум недобавляемым — «Нет».
   */
  roleManager: {
    notGranted: 'Не добавлено',
    notGrantable: 'Нет',
  },

  /**
   * Кнопки. Видимая подпись короткая, доступное имя — с названием права.
   * «Отнять» и «Вернуть» здесь нет намеренно: запрет не ставится (11.10.2026),
   * а лежащий — сбрасывается тем же «Сбросить», что и недействующая поправка.
   */
  action: {
    ALLOW: 'Добавить',
    REMOVE: 'Убрать',
    RESET: 'Сбросить',
    RESET_OWN: 'Сбросить',
    /** `{right}` — название права. */
    ariaLabel: {
      ALLOW: 'Добавить право «{right}»',
      REMOVE: 'Убрать право «{right}»',
      RESET: 'Сбросить поправку по праву «{right}»',
      RESET_OWN: 'Сбросить собственную поправку по праву «{right}»',
    },
  },

  /** Пояснения под названием права. */
  note: {
    /** `{gip}` — ФИО ГИПа (Р-197). */
    inheritedFrom:
      'Закрыто из-за ГИПа: {gip}. У него стоит запрет, а помощник его наследует. Сбросить запрет можно только в карточке ГИПа.',
    /** Собственная строка помощника под запретом ГИПа (решение плана П17). */
    ownUnderInherited:
      'У помощника стоит и собственная поправка. Её можно сбросить — запрет ГИПа при этом останется.',
    /** `{view}` — название парного права (Р-195). */
    viewMissingGranted: 'Не действует без «{view}» — добавьте и его.',
    viewMissingDenied: 'Не действует: отнято «{view}».',
    /** Что стоит в недействующей строке. */
    inertStandsDeny: 'Стоит «отнято».',
    inertStandsAllow: 'Стоит «добавлено».',
    /**
     * Область добавленного права у руководителя отдела (Р-204, Р-217 а).
     * ⚠️ «Куда он сам вошёл», а не «где он руководитель отдела»: в объект
     * руководитель входит сам, своим действием, и право идёт за этим входом.
     */
    scopeObject:
      'Добавленное действует только на объектах, куда он сам вошёл руководителем своего отдела.',
    scopeContracts:
      'Добавленное действует только по договорам объектов, куда он сам вошёл руководителем своего отдела.',
    scopeOrg: 'Добавленное действует по всему бюро.',
  },

  /** Почему действие не предлагается. */
  blocked: {
    /** Р-217 (б). */
    notGrantable:
      'Руководителю отдела не добавляется — это право есть только у администраторов, по должности.',
    /** `{view}` — название парного права (Р-195). */
    needsView: 'Сначала добавьте «{view}».',
  },

  /**
   * Почему человеку права не выдаются (Р-191; умолчание 3 Р-211). Строй
   * (Ф8 · В-Т): первая строка полужирным — `lead`, затем почему — `reason`,
   * затем куда идти — `where` (у кого он есть).
   *
   * `{role}` в причине — название должности (`ROLE_NAMES[roleSlug] ??
   * roleName`): «Инженер», «Ведущий инженер», дремлющий «Генеральный
   * директор» уровня супервайзера — словом «сотрудник» / «супервайзер» их не
   * назвать (фикс-пак 1 В-Т).
   *
   * ⚠️ `PENDING` — только приглашённому РУКОВОДИТЕЛЮ ОТДЕЛА: ему одному после
   * входа будет что добавить, поэтому и первая строка у него другая. Приглашённый
   * сотрудник и супервайзер получают текст своего уровня, приглашённый
   * администратор — то же объяснение, что и действующий (`describeRightsCard`).
   */
  ineligible: {
    lead: {
      SUPERVISOR: NOT_CONFIGURED,
      EMPLOYEE: NOT_CONFIGURED,
      PENDING: 'Права можно будет добавить после первого входа.',
      ARCHIVED: NOT_CONFIGURED,
    },
    reason: {
      SUPERVISOR: 'Должность «{role}» — права к ней не добавляются.',
      EMPLOYEE: 'Должность «{role}» — права к ней не добавляются.',
      PENDING: 'Человек ещё не принял приглашение.',
      ARCHIVED: 'Человек в архиве. Права добавляются только действующим руководителям отделов.',
    },
    /** Куда идти. Приглашённому и архивному — нет: им причина и есть ответ. */
    where: {
      SUPERVISOR: WHERE_TO_ADD,
      EMPLOYEE: WHERE_TO_ADD,
      PENDING: null,
      ARCHIVED: null,
    },
    /**
     * Строки, оставшиеся от прежней должности или статуса. У не-получателя
     * своя строка не действует всегда (сервер: `LEVEL_MISMATCH` либо
     * `NOT_ACTIVE`), и у каждой — «Сбросить» (`actionOf`).
     */
    stuckRows: 'Ниже — старые настройки, которые уже не действуют. Их можно только сбросить.',
  },

  /** Диалог подтверждения — только у «Добавить» общий журнал. */
  confirm: {
    allowTitle: 'Добавить право?',
    /** `{name}` — ФИО, `{right}` — название права. */
    subject: '{name} — «{right}».',
    /**
     * «Добавить» журнал руководителю отдела (Р-190: журнал без фильтра и без
     * экрана; умолчание 1 Р-211: записи о выдаче прав видны в нём наравне;
     * Р-224 и Р-227 (5): окно перечисляет, что журнал открывает, включая
     * личные данные). Состав — `JOURNAL_CONTENTS_RU` выше, общий с подсказкой.
     */
    allowJournal: 'Журнал открывает все записи без фильтра, в том числе:',
    /** Перечень под вступлением — списком (`<ul>`); источник — `JOURNAL_CONTENTS_RU`. */
    allowJournalItems: JOURNAL_CONTENTS_RU,
    allowJournalTail: `${JOURNAL_HIDDEN_RU} Отдельного экрана у журнала нет: он читается запросом к серверу.`,
    cancel: 'Отмена',
    allowSubmit: 'Добавить',
  },

  /** Итог записи. `…Reread` — карточка перечитана; `…Stale` — перечитать не удалось. */
  result: {
    saved: 'Сохранено',
    versionConflictReread:
      'Состояние изменилось, пока вы смотрели. Права перечитаны — проверьте и повторите.',
    versionConflictStale:
      'Состояние изменилось, пока вы смотрели, а перечитать права не удалось. Закройте и откройте карточку заново.',
    busy: 'Не удалось сохранить из-за одновременных изменений. Повторите.',
    targetNotEligible:
      'Этому человеку сейчас нельзя добавить право: он не действующий руководитель отдела.',
    /**
     * ⚠️ ЗАПАСНОЙ: печатается, только если сервер не прислал своего текста.
     * Причин у отказа несколько (право не добавляется, сперва нужно «видеть»,
     * запрет не ставится), и какая сработала, знает только сервер.
     */
    effectNotApplicable: 'К этой должности такое действие не применяется.',
    userNotFound: 'Человек не найден — возможно, приглашение удалили.',
    forbidden: 'Менять права может только супервайзер.',
    /** Связь оборвалась: дошёл ли запрос, неизвестно. */
    networkReread:
      'Не удалось подтвердить изменение: связь оборвалась. Права перечитаны — проверьте строку.',
    networkStale:
      'Не удалось подтвердить изменение: связи с сервером нет. Сохранилось ли оно, неизвестно — проверьте строку, когда связь вернётся.',
    unknown: 'Не удалось сохранить. Повторите попытку.',
  },

  /** Карточка человека не открылась (ссылка из сводного списка, чужой человек). */
  card: {
    /**
     * 404. Сервер отвечает им и на несуществующего человека, и на недоступного
     * (руководитель отдела и человек чужого отдела) — намеренно, чтобы не
     * подтверждать существование. Поэтому текст причину НЕ называет: прежнее
     * «возможно, приглашение удалили» руководителю про живого коллегу было
     * неправдой.
     */
    notFound: 'Карточка недоступна или человек не найден.',
    forbidden: 'Эта карточка вам недоступна.',
    loadError: 'Не удалось открыть карточку.',
    close: 'Закрыть',
    retry: 'Повторить',
  },

  /** Предупреждение о сбросе прав — в формах, меняющих должность и статус (Р-198, П3). */
  resetWarning: {
    roleChange: 'Индивидуальные права человека, если они есть, будут сброшены.',
    reinvite: 'Индивидуальные права человека, если они есть, будут сброшены.',
  },

  // ─── Сводный список у супервайзера ────────────────────────────────────────
  page: {
    nav: 'Права доступа',
    title: 'Права доступа',
    /**
     * Ф8 · В-Т: подзаголовок говорит, КТО в списке. В списке — все, у кого
     * лежит поправка: штатно руководители отделов, плюс строки прежних времён
     * (фикс-пак 1: прежнее «руководители отделов» про них молчало).
     */
    subtitle:
      'Здесь руководители отделов, которым добавлены права. Строки со значками «Отнято», «Закрыто из-за ГИПа» и «Не действует» — старые настройки; что с ними делать, написано в карточке.',
    /** Только при загруженном непустом списке — щёлкать есть по чему. */
    subtitleOpen: 'Нажмите на имя, чтобы открыть карточку.',
    subtitleHow: `Где добавлять права: ${PATH_TO_RIGHTS}.`,
    loading: 'Список загружается',
    loadError: 'Не удалось загрузить список',
    loadErrorHint: 'Попробуйте обновить страницу.',
    emptyTitle: 'Пока никому ничего не добавлено',
    emptyHint: `Права добавляются только руководителям отделов: ${PATH_TO_RIGHTS}.`,
    filteredEmptyTitle: 'Никого не нашлось',
    filteredEmptyHint: 'Измените поиск или выберите другое право.',
    resetFilters: 'Сбросить фильтр',

    searchLabel: 'Поиск по имени',
    searchPlaceholder: 'Поиск по имени',
    rightFilterLabel: 'Право',
    rightFilterAll: 'Все права',

    /** `{name}` — ФИО. */
    openCard: 'Открыть карточку: {name}',
    listAriaLabel: 'Добавленные права и старые настройки',

    statusPending: 'Ожидает',
    statusArchived: 'Архив',

    /**
     * Подписи значков в строке списка. «Отнято» и «Закрыто из-за ГИПа» — показ
     * запрета прежних времён: поставить его нельзя, сбросить — в карточке.
     */
    chipDenied: 'Отнято',
    chipGranted: 'Добавлено',
    chipInert: 'Не действует',
    /** `{gip}` — ФИО ГИПа. */
    chipInherited: 'Закрыто из-за ГИПа: {gip}',
    chipInheritedNoName: 'Закрыто из-за ГИПа',
    /** `{view}` — название парного права (Р-195). */
    viewMissing: 'Не действует без «{view}».',
    /** Коротко — где действует добавленное руководителю отдела (Р-204). */
    scopeObject: 'Только на объектах, куда он сам вошёл.',
    scopeContracts: 'Только по договорам объектов, куда он сам вошёл.',
  },
} as const;
